export type SessionType = 'practice' | 'learning' | 'test' | 'assignment' | 'review' | 'daily';

/** Focused single-question flows embedded in SessionPage. They render with the
 *  practice behavioral profile (`sessionType: 'practice'`) but their own chrome —
 *  so they're config lookup keys, not values of the behavioral `sessionType` field. */
export type FocusedSessionKind = 'firstRun' | 'quiz';

/** Anything `getSessionConfig` accepts: a behavioral session type or a focused kind. */
export type SessionConfigKey = SessionType | FocusedSessionKind;

export type SolutionStylePreference = 'detailed' | 'formal' | 'quick';

export interface QuestionSessionConfig {
  sessionType: SessionType;

  // Default solution style shown in feedback
  defaultSolutionStyle: SolutionStylePreference;

  // ── Analysis lifecycle ────────────────────────────────────────────────
  /** Analyze the outgoing question in the background when the student leaves it
   *  (Next / navigation). Off for review (nothing to analyze). */
  analyzeOnLeave: boolean;
  /** On Finish / back, wait for any in-flight analysis to complete before
   *  closing — soft: the student can still close, analysis finishes server-side. */
  waitForAnalysisOnEnd: boolean;

  // Buttons
  showAnalyzeButton: boolean;
  showHintButton: boolean;
  showMarkForReview: boolean;
  showBackButton: boolean;
  showEndButton: boolean;
  showSaveAndExit: boolean;
  showDoneButton: boolean;
  /** Toolbar chip + picker that lets the student copy a past attempt's
   *  solution into the live editor. Practice mode only — see
   *  docs/FeatureDesigns/assignments-tests/load-past-attempt.md. */
  showLoadPastAttempt: boolean;

  // Timer
  showCountdownTimer: boolean;
  showElapsedTimer: boolean;

  // Feedback
  showImmediateFeedback: boolean;
  /** MCQ with written work but no option picked: warn before leaving/submitting.
   *  Test only — there the un-recorded option costs marks and can't be fixed
   *  afterwards. Everywhere else the student gets immediate feedback and can
   *  pick and resubmit, so the prompt is just noise. */
  confirmMCQWithoutAnswer: boolean;
  /** Analysis "extras" tabs beyond score + solution. Off in focused modes
   *  (first-run / quiz / placement) so the analysis stays on the result. */
  showDiscuss: boolean;
  showDeepenUnderstanding: boolean;
  /** The inline analysis/feedback panel shown after submit. Off in quiz
   *  (the score is recorded silently and the flow advances). */
  showAnalysisFeedbackPanel: boolean;
  /** After a wrong answer, invite the student to tag the question (desktop
   *  only, budgeted per student). Off where the student is being examined or is
   *  re-reading a finished sitting. See
   *  docs/FeatureDesigns/wrong-answer-tag-nudge-target.md. */
  offerTagNudge: boolean;

  // Focused single-question chrome (first-run / quiz / placement). Normal
  // sessions use the standard back button instead.
  /** A skip/exit affordance in the header (replaces the back button). */
  showSkipExit: boolean;
  skipExitLabel: string;

  /** The question-number strip in the header. Which strip renders (numbered
   *  circles vs practice dots) follows from the session; this only decides
   *  whether one appears at all. */
  showQuestionStrip: boolean;

  // Session header
  /** Placement (quiz) shows an "N of M" progress indicator INSIDE the solve
   *  header — no separate chrome bar. SessionPage derives the label + counter
   *  from the seeded session + current index; this flag decides whether that
   *  in-header progress renders at all. */
  showProgressHeader: boolean;

  // Labels
  endButtonTitle: string;
  endConfirmTitle: string;
  endConfirmAction: string;
  completionTitle: string;
}

export function getSessionConfig(type: SessionConfigKey): QuestionSessionConfig {
  switch (type) {
    case 'test':
      return {
        sessionType: 'test',
        analyzeOnLeave: false, waitForAnalysisOnEnd: false,
        defaultSolutionStyle: 'formal',
        showAnalyzeButton: false,
        showHintButton: false,
        showMarkForReview: true,
        showBackButton: false,
        showEndButton: true,
        showSaveAndExit: false,
        showDoneButton: false,
        showLoadPastAttempt: false,
        showCountdownTimer: true,
        showElapsedTimer: false,
        showImmediateFeedback: false,
        confirmMCQWithoutAnswer: true,
        showDiscuss: true,
        showDeepenUnderstanding: true,
        showAnalysisFeedbackPanel: true,
        offerTagNudge: false,
        showSkipExit: false,
        skipExitLabel: '',
        showQuestionStrip: true,
        showProgressHeader: false,
        endButtonTitle: 'End Test',
        endConfirmTitle: 'End Test?',
        endConfirmAction: 'End Test',
        completionTitle: 'Test Complete!',
      };
    case 'daily':
      // Daily practice session (purpose:'daily-plan'). Practice-style: immediate
      // feedback, hints, learning tabs on. Leaving = resume later (back button), and
      // a lightweight "Finish" closes early. No marks/countdown, no Save & Exit.
      return {
        sessionType: 'daily',
        analyzeOnLeave: false, waitForAnalysisOnEnd: false,
        defaultSolutionStyle: 'quick',
        showAnalyzeButton: true,
        showHintButton: true,
        showMarkForReview: false,
        showBackButton: true,
        showEndButton: true,
        showSaveAndExit: false,
        showDoneButton: false,
        showLoadPastAttempt: false,
        showCountdownTimer: false,
        showElapsedTimer: true,
        showImmediateFeedback: true,
        confirmMCQWithoutAnswer: false,
        showDiscuss: true,
        showDeepenUnderstanding: true,
        showAnalysisFeedbackPanel: true,
        offerTagNudge: true,
        showSkipExit: false,
        skipExitLabel: '',
        showQuestionStrip: true,
        showProgressHeader: false,
        endButtonTitle: 'Finish',
        endConfirmTitle: 'Finish session?',
        endConfirmAction: 'Finish',
        completionTitle: 'Nice work!',
      };
    case 'assignment':
      return {
        sessionType: 'assignment',
        analyzeOnLeave: false, waitForAnalysisOnEnd: false,
        defaultSolutionStyle: 'quick',
        showAnalyzeButton: true,
        showHintButton: true,
        showMarkForReview: false,
        showBackButton: true,
        showEndButton: true,
        showSaveAndExit: true,
        showDoneButton: false,
        showLoadPastAttempt: false,
        showCountdownTimer: false,
        showElapsedTimer: true,
        showImmediateFeedback: true,
        confirmMCQWithoutAnswer: false,
        showDiscuss: true,
        showDeepenUnderstanding: true,
        showAnalysisFeedbackPanel: true,
        offerTagNudge: true,
        showSkipExit: false,
        skipExitLabel: '',
        showQuestionStrip: true,
        showProgressHeader: false,
        endButtonTitle: 'Submit',
        endConfirmTitle: 'Submit Assignment?',
        endConfirmAction: 'Submit',
        completionTitle: 'Assignment Complete!',
      };
    case 'review':
      return {
        sessionType: 'review',
        analyzeOnLeave: false, waitForAnalysisOnEnd: false,
        defaultSolutionStyle: 'formal',
        showAnalyzeButton: false,
        showHintButton: false,
        showMarkForReview: false,
        showBackButton: true,
        showEndButton: false,
        showSaveAndExit: false,
        showDoneButton: true,
        showLoadPastAttempt: false,
        showCountdownTimer: false,
        showElapsedTimer: false,
        showImmediateFeedback: false,
        confirmMCQWithoutAnswer: false,
        showDiscuss: true,
        showDeepenUnderstanding: true,
        showAnalysisFeedbackPanel: true,
        offerTagNudge: false,
        showSkipExit: false,
        skipExitLabel: '',
        showQuestionStrip: true,
        showProgressHeader: false,
        endButtonTitle: '',
        endConfirmTitle: '',
        endConfirmAction: '',
        completionTitle: '',
      };
    case 'practice':
    case 'learning':
      return {
        sessionType: type,
        analyzeOnLeave: true, waitForAnalysisOnEnd: true,
        defaultSolutionStyle: 'quick',
        showAnalyzeButton: true,
        showHintButton: true,
        showMarkForReview: false,
        showBackButton: true,
        showEndButton: true,
        showSaveAndExit: false,
        showDoneButton: false,
        showLoadPastAttempt: type === 'practice',
        showCountdownTimer: false,
        showElapsedTimer: false,
        showImmediateFeedback: true,
        confirmMCQWithoutAnswer: false,
        showDiscuss: true,
        showDeepenUnderstanding: true,
        showAnalysisFeedbackPanel: true,
        offerTagNudge: true,
        showSkipExit: false,
        skipExitLabel: '',
        showQuestionStrip: true,
        // Reality, not aspiration: this flag was declared but never read, so
        // practice has always kept its strip up alongside the feedback panel.
        // Wiring it as `false` would have been an unrelated behaviour change
        // smuggled in with the review fix.
        showProgressHeader: false,
        endButtonTitle: 'Finish',
        endConfirmTitle: 'Finish practice?',
        endConfirmAction: 'Finish',
        completionTitle: '',
      };
    case 'firstRun':
    case 'quiz': {
      // Focused single-question chrome. Both render on the practice behavioral
      // profile (sessionType stays 'practice'); they only trim the chrome:
      // skip/exit instead of a back button, no analysis "extras". They are
      // single-question flows, so no "Finish" end button and no load-past-attempt.
      // The two differ only in the skip label and (quiz) the silent-score flow.
      const base = getSessionConfig('practice');
      return {
        ...base,
        showBackButton: false,
        showEndButton: false,
        showLoadPastAttempt: false,
        showDiscuss: false,
        showDeepenUnderstanding: false,
        showSkipExit: true,
        skipExitLabel: type === 'quiz' ? 'Exit' : 'Skip',
        showAnalysisFeedbackPanel: type !== 'quiz',
        // Focused flows stay on the result; placement is an assessment.
        offerTagNudge: false,
        // Placement (quiz) is an N-of-M flow → progress in-header; first-run is a
        // single question → none.
        showProgressHeader: type === 'quiz',
        endButtonTitle: '',
      };
    }
  }
}
